import type { ComponentProps, ComponentType } from "react";
import {
  Bar as VBar,
  BarRounded as VBarRounded,
  Circle as VCircle,
  Line as VLine,
  LinePath as VLinePath,
  Area as VArea,
  AreaClosed as VAreaClosed,
  Arc as VArc,
  Pie as VPie,
  Polygon as VPolygon,
} from "@visx/shape";
import type {
  BarRoundedProps,
  LineProps,
  LinePathProps,
  AreaProps,
  AreaClosedProps,
  ArcProps,
  PieProps,
  PolygonProps,
} from "@visx/shape";
import { localPoint } from "@visx/event";
import type { PieArcDatum } from "@visx/vendor/d3-shape";
import { useChartColors, type ChartColorToken } from "./colors";
import type { Closed } from "./internal/strip";
import type { Omittable } from "../../lib/props";

/** Bridge visx's generic shape components (which take open SVGProps) to our
 * closed, token-themed public surface. */
type AnyComp = ComponentType<Record<string, unknown>>;

function useFill(colorToken?: ChartColorToken): string {
  const c = useChartColors();
  return c.paint(colorToken);
}

/* --------------------------------------------------------------- fill marks */

// `Bar`/`Circle` keep their geometry (`x`/`y`/`width`/`height`/`rx`, `cx`/`cy`/`r`)
// in the SVG-attribute spread rather than in their exported `*Props`, so we close
// over the full component props to preserve it while still stripping the style/color
// escape hatches. The other marks carry geometry in their own `*Props`.
export type BarMarkProps = Closed<ComponentProps<typeof VBar>> & Omittable<{ colorToken?: ChartColorToken }>;
/** A themed `<rect>`. Geometry via `x`/`y`/`width`/`height`; color via `colorToken`. */
export function Bar({ colorToken, ...rest }: BarMarkProps) {
  const Comp = VBar as AnyComp;
  return <Comp {...rest} fill={useFill(colorToken)} />;
}

export type HitRectProps = Pick<
  ComponentProps<typeof VBar>,
  "x" | "y" | "width" | "height" | "rx" | "ry"
>;
/**
 * Invisible rectangular hit target for chart interactions — stretch ONE over the
 * whole plot (inside an event-carrying `ChartGroup`) and derive the hovered cell/band
 * from `localPoint` math, instead of attaching handlers per mark.
 */
export function HitRect(props: HitRectProps) {
  const Comp = VBar as AnyComp;
  return <Comp {...props} fill="transparent" />;
}

export type BarRoundedMarkProps = Closed<BarRoundedProps> & Omittable<{ colorToken?: ChartColorToken }>;
/** A themed rounded `<rect>` (`radius` + which corners). */
export function BarRounded({ colorToken, ...rest }: BarRoundedMarkProps) {
  const Comp = VBarRounded as AnyComp;
  return <Comp {...rest} fill={useFill(colorToken)} />;
}

export type CircleMarkProps = Closed<ComponentProps<typeof VCircle>> & Omittable<{ colorToken?: ChartColorToken }>;
/** A themed `<circle>` (`cx`/`cy`/`r`). */
export function Circle({ colorToken, ...rest }: CircleMarkProps) {
  const Comp = VCircle as AnyComp;
  return <Comp {...rest} fill={useFill(colorToken)} />;
}

export type PolygonMarkProps = Closed<PolygonProps> & Omittable<{ colorToken?: ChartColorToken }>;
/** A themed polygon (`sides`/`size` or `points`). */
export function Polygon({ colorToken, ...rest }: PolygonMarkProps) {
  const Comp = VPolygon as AnyComp;
  return <Comp {...rest} fill={useFill(colorToken)} />;
}

export type AreaMarkProps<Datum> = Closed<AreaProps<Datum>> & Omittable<{ colorToken?: ChartColorToken }>;
/** A themed area path (fill only; use `AreaClosed` to baseline-fill). */
export function Area<Datum>({ colorToken, ...rest }: AreaMarkProps<Datum>) {
  const Comp = VArea as AnyComp;
  return <Comp {...rest} fill={useFill(colorToken)} />;
}

export type AreaClosedMarkProps<Datum> = Closed<AreaClosedProps<Datum>> & Omittable<{
  colorToken?: ChartColorToken;
}>;
/** A themed area closed to the scale baseline (`yScale` required). */
export function AreaClosed<Datum>({ colorToken, ...rest }: AreaClosedMarkProps<Datum>) {
  const Comp = VAreaClosed as AnyComp;
  return <Comp {...rest} fill={useFill(colorToken)} />;
}

export type ArcMarkProps<Datum> = Closed<ArcProps<Datum>> & Omittable<{ colorToken?: ChartColorToken }>;
/** A themed arc/wedge (donut, gauge). Radii/angles via `innerRadius`/`outerRadius`/…. */
export function Arc<Datum>({ colorToken, ...rest }: ArcMarkProps<Datum>) {
  const Comp = VArc as AnyComp;
  return <Comp {...rest} fill={useFill(colorToken)} />;
}

export type PieMarkProps<Datum> = Closed<Omit<PieProps<Datum>, "fill">> & Omittable<{
  /** Single-color override; by default slices cycle the theme ramp by arc index. */
  colorToken?: ChartColorToken;
  /** Per-slice color, same convention as the XYChart series accessor: return a
   *  RESOLVED theme rgb (`useCategoricalColors().colors`/`scale`, never a raw
   *  hex). Falls back to `colorToken`, then the ramp cycle. */
  colorAccessor?: (d: Datum, index: number) => string | null | undefined;
  /** Slice click — the slices get a pointer cursor. */
  onArcClick?: (arc: PieArcDatum<Datum>) => void;
  /** Pointer moved over a slice (`point` is svg-relative — feed it to
   *  `ChartTooltipCard`) or left the pie entirely (`null, null`). */
  onArcHover?: (arc: PieArcDatum<Datum> | null, point: { x: number; y: number } | null) => void;
  /** Focus one slice by arc index — that slice brightens slightly; the rest
   *  stay untouched. Pair with `onArcHover` for hover focus. */
  activeIndex?: number | null;
}>;
/** The computed arc for one pie slice — passed to `Pie`'s `centroid`/`children`
 * render props (`arc.data` is your datum, `arc.startAngle`/`endAngle` the wedge). */
export type { PieArcDatum } from "@visx/vendor/d3-shape";

/** visx `Pie`'s children render-prop args (loose local mirror — the generator
 * carries `.centroid()` alongside the path callable). */
type PieProvided<Datum> = {
  arcs: PieArcDatum<Datum>[];
  path: ((arc: PieArcDatum<Datum>) => string | null) & {
    centroid: (arc: PieArcDatum<Datum>) => [number, number];
  };
};

/**
 * A themed pie/donut. Slices auto-color from the ramp; `colorAccessor` colors
 * each slice (entity-sliced pies), `colorToken` forces one color. Passing any
 * of `onArcClick`/`onArcHover`/`activeIndex` switches to kit-rendered slices
 * (same geometry) with the interaction wired per arc — `centroid` labels keep
 * working in both modes.
 */
export function Pie<Datum>({
  colorToken,
  colorAccessor,
  onArcClick,
  onArcHover,
  activeIndex,
  centroid,
  top,
  left,
  ...rest
}: PieMarkProps<Datum>) {
  const c = useChartColors();
  const single = c.resolve(colorToken);
  const Comp = VPie as AnyComp;
  const arcFill = (arc: PieArcDatum<Datum>) =>
    colorAccessor?.(arc.data, arc.index) ?? single ?? c.ramp[arc.index % c.ramp.length];

  if (!onArcClick && !onArcHover && activeIndex == null) {
    return <Comp {...rest} top={top} left={left} centroid={centroid} fill={arcFill} />;
  }

  // visx only wraps its own arc rendering in the `top`/`left` Group — a children
  // render-prop bypasses it — so the kit-rendered slices re-apply the translation.
  return (
    <Comp {...rest}>
      {({ arcs, path }: PieProvided<Datum>) => (
        <g transform={`translate(${left ?? 0}, ${top ?? 0})`}>
          {arcs.map((arc) => (
            <g key={arc.index}>
              <path
                // `?? undefined` is a backstop: `arcs` and `path` come from
                // the same d3 pie layout, so the generator has a path for
                // every arc it was handed.
                d={path(arc) ?? undefined}
                fill={arcFill(arc)}
                // Highlight = the hovered wedge brightens in place; geometry
                // and every neighbor stay untouched. A filter (not a computed
                // fill) so it works for any ramp/token color in both modes.
                style={
                  activeIndex === arc.index ? { filter: "brightness(1.15)" } : undefined
                }
                cursor={onArcClick ? "pointer" : undefined}
                onClick={onArcClick ? () => onArcClick(arc) : undefined}
                onMouseMove={
                  onArcHover
                    ? (event) => {
                        const p = localPoint(event);
                        // The `: null` arm is a backstop: `localPoint` falls
                        // back to the node's bounding box for any real event,
                        // so it answers null only for one with no target at
                        // all. The null stays in `onArcHover`'s signature
                        // because callers pass this result straight through to
                        // `useChartHover().show`, which ignores a null point.
                        onArcHover(arc, p ? { x: p.x, y: p.y } : null);
                      }
                    : undefined
                }
                onMouseLeave={onArcHover ? () => onArcHover(null, null) : undefined}
              />
              {centroid ? centroid(path.centroid(arc), arc) : null}
            </g>
          ))}
        </g>
      )}
    </Comp>
  );
}

/* ------------------------------------------------------------- stroke marks */

export type LinePathMarkProps<Datum> = Closed<LinePathProps<Datum>> & Omittable<{
  colorToken?: ChartColorToken;
}>;
/** A themed line path (`data` + `x`/`y` accessors, optional `curve`). */
export function LinePath<Datum>({ colorToken, ...rest }: LinePathMarkProps<Datum>) {
  const Comp = VLinePath as AnyComp;
  return <Comp {...rest} stroke={useFill(colorToken)} fill="none" />;
}

export type LineMarkProps = Closed<LineProps> & Omittable<{ colorToken?: ChartColorToken }>;
/** A themed straight `<line>` (`from`/`to` points). */
export function Line({ colorToken, ...rest }: LineMarkProps) {
  const Comp = VLine as AnyComp;
  return <Comp {...rest} stroke={useFill(colorToken)} />;
}

/* ----------------------------------- containers & links (context/data-driven) */
// Re-exported verbatim: stacks/groups are colored by their series children or an
// ordinal scale, and the Link* family is geometry-only path helpers. Names match
// the visx/d3 corpus. Raw color literals in these are caught by check-edit.
// (`BarStack`/`BarGroup`/`AreaStack` are the XYChart series containers — exported
// from ./xychart. Shape's standalone `*Horizontal` variants are unique names.)
export {
  Stack,
  BarStackHorizontal,
  BarGroupHorizontal,
  LineRadial,
  SplitLinePath,
  LinkHorizontal,
  LinkVertical,
  LinkRadial,
  LinkHorizontalCurve,
  LinkVerticalCurve,
  LinkRadialCurve,
  LinkHorizontalLine,
  LinkVerticalLine,
  LinkRadialLine,
  LinkHorizontalStep,
  LinkVerticalStep,
  LinkRadialStep,
} from "@visx/shape";
